import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/movies";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused]);

  const go = (direction: number) => {
    setActive((prev) => (prev + direction + heroSlides.length) % heroSlides.length);
  };

  const slide = heroSlides[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative min-h-[560px] w-full md:min-h-[620px]">
        {heroSlides.map((item, index) => (
          <div
            key={item.movieId}
            className={`absolute inset-0 transition-opacity duration-[900ms] ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={index !== active}
          >
            <img
              src={item.image}
              alt={`${item.title} 焦点海报`}
              title={`${item.title} 高清在线观看`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}

        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/65 to-foreground-950/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/25" />

        <div className="relative z-10 mx-auto flex w-full max-w-[1280px] flex-col justify-center px-4 pb-20 pt-10 md:px-6 md:pb-16 md:pt-12">
          <div className="w-full max-w-[620px]">
            <div className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
              <span className="font-label text-[11px] uppercase tracking-[0.2em] text-background-100">
                High Definition Cinema
              </span>
            </div>

            <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-[44px]">
              蜜桃成熟时在线观看
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-background-200 md:text-base">
              高清电影在线观看平台，按类型、地区、年份快速筛选，把找片的时间留给看电影。
            </p>

            <div key={slide.movieId} className="animate-soft-in mt-8 border-l-2 border-primary-400 pl-4">
              <p className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-300">
                {slide.kicker}
              </p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                {slide.title}
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-background-200/90">
                {slide.description}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {slide.badges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-background-50/20 bg-background-50/10 px-2.5 py-1 font-label text-[11px] text-background-100"
                  >
                    {badge}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slide.movieId}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill text-base w-4 h-4 flex items-center justify-center" />
                立即观看
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-5 py-3 text-sm text-background-100 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
              >
                浏览全部分类
                <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10">
          <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between px-4 md:px-6">
            <div className="flex items-center gap-2">
              {heroSlides.map((item, index) => (
                <button
                  key={item.movieId}
                  type="button"
                  aria-label={`切换到 ${item.title}`}
                  onClick={() => setActive(index)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all duration-500 ${
                    index === active ? "w-8 bg-primary-400" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                  }`}
                />
              ))}
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <span className="mr-1 font-label text-xs tracking-widest text-background-200/80">
                {String(active + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
              </span>
              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(-1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-left-s-line text-lg w-5 h-5 flex items-center justify-center" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-100 transition-colors hover:bg-background-50/15"
              >
                <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}